import { useState } from 'react';
import { mormSigner, SignResult } from '../sdk';

/**
 * Custom hook for managing message signing functionality
 * 
 * @returns Object containing:
 *   - mnemonic: Current mnemonic phrase
 *   - setMnemonic: Function to update mnemonic
 *   - message: Current message to sign
 *   - setMessage: Function to update message
 *   - useEIP712: Whether to use EIP-712 typed data
 *   - setUseEIP712: Function to toggle EIP-712 mode
 *   - addressIndex: Current address index
 *   - setAddressIndex: Function to update address index
 *   - result: Sign result (success or error)
 *   - loading: Whether signing is in progress
 *   - signMessage: Function to sign the message
 *   - isReady: Boolean indicating if WASM is ready
 */
export function useMessageSigner() {
  const [mnemonic, setMnemonic] = useState<string>('abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about');
  const [message, setMessage] = useState<string>('Hello, World!');
  const [useEIP712, setUseEIP712] = useState<boolean>(false);
  const [addressIndex, setAddressIndex] = useState<number>(0);
  const [result, setResult] = useState<SignResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const signMessage = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: 'WASM module not ready'
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const options = {
        addressIndex,
        useEIP712
      };

      const signResult = useEIP712 
        ? await mormSigner.signEIP712(mnemonic, message, options)
        : await mormSigner.signMessage(mnemonic, message, options);
      
      setResult(signResult);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    mnemonic,
    setMnemonic,
    message,
    setMessage,
    useEIP712,
    setUseEIP712,
    addressIndex,
    setAddressIndex,
    result,
    loading,
    signMessage,
    isReady: mormSigner.isReady()
  };
}
